import { type PremiumTier, premiumTierName } from '@src/components/PremiumBadge/tierIdentity';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Badge } from '@src/components/Badge/Badge';
import { PremiumBadge } from '@src/components/PremiumBadge/PremiumBadge';
import { classes } from '@src/components/layout/space';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import { isInAppHref } from '@src/routing/href';
import { isSeason, DEFAULT_GAME_SEASON, type Season } from '@src/theme/season';
import { gameCardArtwork } from '@src/theme/gameCards';
import './GameCard.css';

export interface GameCardProps {
  href?: string;
  season?: Season;
  picture: ReactNode;
  name: string;
  meta: string[];
  badge?: string;
  premiumLabel?: string;
  premiumTier?: PremiumTier;
  archived?: boolean;
  testId?: string;
}

/** A game on My games: picture, name, and grey facts, like v1 UserCard. */
export function GameCard({
  season: savedSeason,
  href,
  picture,
  name,
  meta,
  badge,
  premiumLabel,
  premiumTier,
  archived = false,
  testId,
}: GameCardProps) {
  const season = savedSeason && (isSeason(savedSeason) ? savedSeason : DEFAULT_GAME_SEASON);
  const { i18n } = useTranslation();
  const artwork = season ? gameCardArtwork[season] : undefined;
  const body = (
    <>
      {artwork && (
        <span className="game-card__scenery" aria-hidden="true">
          <span className="game-card__scenery-left"><img src={artwork.left} alt="" /></span>
          <span className="game-card__scenery-right"><img src={artwork.right} alt="" /></span>
        </span>
      )}
      {badge ? (
        <Badge tone="info" testId={testIdFor(testId, 'badge')}>
          {badge}
        </Badge>
      ) : null}
      {premiumLabel ? <PremiumBadge tier={premiumTier} className="game-card__premium">{premiumTier ? premiumTierName(premiumTier, i18n.language.startsWith('ru')) : premiumLabel}</PremiumBadge> : null}
      <span className="game-card__pic">{picture}</span>
      <span className="game-card__copy">
        <Typography as="span" role="title/card" className="game-card__name">
          {name}
        </Typography>
        {meta.length ? (
          <Typography as="span" role="meta/helper" className="game-card__meta">
            {meta.join(' · ')}
          </Typography>
        ) : null}
      </span>
    </>
  );

  const className = classes(
    'game-card',
    season && 'game-card--themed',
    season && `game-card--${season}`,
    badge && 'game-card--badged',
    archived && 'game-card--archived',
  );

  if (!href) return <article className={className} {...testIdAttr(testId)}>{body}</article>;

  if (isInAppHref(href)) {
    return (
      <RouterLink className={className} to={href} {...testIdAttr(testId)}>
        {body}
      </RouterLink>
    );
  }

  return (
    <a className={className} href={href} {...testIdAttr(testId)}>
      {body}
    </a>
  );
}
